/**
 * User Service API
 * 사용자 관리 API 서비스 (기관 관리용)
 */

import { apiClient } from './apiClient';
import type {
	GetAllUserCountResponseDto,
	GetUserListWithFiltersResponseDto,
	GetOrganizationUserListResponseDto,
	GetUserListResponseDto,
	RegisterOrganizationMemberRequestDto,
	RegisterOrganizationMemberResponseDto,
	UpdateUserStatusRequestDto,
	UpdateUserStatusResponseDto,
	DeleteUserListRequestDto,
	DeleteUserListResponseDto,
	RemoveUserOrganizationRequestDto,
	RemoveUserOrganizationResponseDto,
	BulkCreateUsersRequestDto,
	BulkCreateUsersResponseDto,
	CreateGuestAccountsRequestDto,
	CreateGuestAccountsResponseDto,
	ExportUserListExcelRequestDto,
	ExportUserListExcelResponseDto,
	DownloadUserBulkUploadTemplateResponseDto,
	UpdateUserInfoRequestDto,
	UpdateUserInfoResponseDto,
} from '../types/api';
import { normalizeBulkCreateGuestResponse } from '@/utils/guestBulkCreateAlert';

export interface GetUserListFilters {
	page: number;
	pageSize: number;
	/** 검색어 — 백엔드 파라미터명: searchText */
	search?: string;
	/**
	 * 역할 그룹 필터 (GET /user/option/list: roleGroupFilter)
	 * ROLE_ZERO_ONE = Student (role 0, 1)
	 * ROLE_TWO = Master (role 2)
	 */
	roleGroupFilter?: 'ROLE_ZERO_ONE' | 'ROLE_TWO';
	isActive?: boolean;
	/**
	 * 기관 소속 필터 (GET /user/option/list: organizationFilter)
	 * HAS_ORGANIZATION = 기관 소속
	 * NO_ORGANIZATION = 기관 미소속
	 */
	organizationFilter?: 'HAS_ORGANIZATION' | 'NO_ORGANIZATION';
	/** 회원 구분 — 정회원(REGULAR) / 게스트(ASSOCIATE), OpenAPI·백엔드가 지원할 때만 서버 필터 */
	type?: 'REGULAR' | 'ASSOCIATE';
	/** 최근 30일 이내 신규 가입자 필터 */
	isNewUser?: boolean;
	/** 국가 필터 */
	countryId?: number;
	startCreatedAt?: string;
	endCreatedAt?: string;
	startLastSigninAt?: string;
	endLastSigninAt?: string;
	/** 기관 ID 필터 (어드민이 특정 기관의 사용자 조회 시 사용) */
	organizationId?: number;
}

export interface GetOrganizationUserListParams {
	organizationId: number;
	page: number;
	pageSize: number;
	language?: string;
}

export interface GetRegularMemberListParams {
	page: number;
	pageSize: number;
	/** 검색어 — API 파라미터명은 searchText */
	search?: string;
}

export interface UpdateUserRoleRequestDto {
	userId: number;
	role: number; // 0~5
}

export interface UpdateUserRoleResponseDto {
	message: string;
}

/**
 * 사용자 관리 서비스 (기관 관리용)
 */
export const userServiceApi = {
	/**
	 * 전체 유저 수 조회
	 */
	getAllCount: async (): Promise<GetAllUserCountResponseDto> => {
		return await apiClient.get<GetAllUserCountResponseDto>('/user/all/count');
	},

	/**
	 * 조건별 유저 리스트 조회
	 * GET /user/option/list — 일부 백엔드는 검색어 키로 `searchText`만 인식 (`search` 무시 → 필터 조합 시 빈 목록)
	 */
	getListWithFilters: async (params: GetUserListFilters): Promise<GetUserListWithFiltersResponseDto> => {
		const {
			search,
			roleGroupFilter,
			organizationFilter,
			organizationId,
			type,
			isActive,
			isNewUser,
			countryId,
			startCreatedAt,
			endCreatedAt,
			startLastSigninAt,
			endLastSigninAt,
			page,
			pageSize,
		} = params;
		const trimmed = typeof search === 'string' ? search.trim() : '';
		return await apiClient.get<GetUserListWithFiltersResponseDto>('/user/option/list', {
			params: {
				page,
				pageSize,
				...(trimmed ? { searchText: trimmed } : {}),
				...(roleGroupFilter !== undefined ? { roleGroupFilter } : {}),
				...(organizationFilter !== undefined ? { organizationFilter } : {}),
				...(organizationId !== undefined ? { organizationId } : {}),
				...(type !== undefined ? { type } : {}),
				...(isActive !== undefined ? { isActive } : {}),
				...(isNewUser !== undefined ? { isNewUser } : {}),
				...(countryId !== undefined ? { countryId } : {}),
				...(startCreatedAt ? { startCreatedAt } : {}),
				...(endCreatedAt ? { endCreatedAt } : {}),
				...(startLastSigninAt ? { startLastSigninAt } : {}),
				...(endLastSigninAt ? { endLastSigninAt } : {}),
			},
		});
	},

	/**
	 * 기관별 유저 리스트 조회
	 */
	getOrganizationUserList: async (
		params: GetOrganizationUserListParams
	): Promise<GetOrganizationUserListResponseDto> => {
		return await apiClient.get<GetOrganizationUserListResponseDto>(
			`/user/organization/${params.organizationId}/list`,
			{
				params: {
					organizationId: params.organizationId,
					page: params.page,
					pageSize: params.pageSize,
					...(params.language ? { language: params.language } : {}),
				},
			}
		);
	},

	/**
	 * 기관이 없는 정회원 리스트 조회
	 */
	getRegularMemberList: async (params: GetRegularMemberListParams): Promise<GetUserListResponseDto> => {
		const { search, ...rest } = params;
		return await apiClient.get<GetUserListResponseDto>('/user/regular-member/list', {
			params: { ...rest, ...(search ? { searchText: search } : {}) },
		});
	},

	/**
	 * 기관별 정회원 등록
	 */
	registerOrganizationMembers: async (
		data: RegisterOrganizationMemberRequestDto
	): Promise<RegisterOrganizationMemberResponseDto> => {
		return await apiClient.post<RegisterOrganizationMemberResponseDto>(
			'/user/organization-member/list',
			data
		);
	},

	/**
	 * 유저 상태 전환 (활성/비활성)
	 */
	updateUserStatus: async (data: UpdateUserStatusRequestDto): Promise<UpdateUserStatusResponseDto> => {
		return await apiClient.post<UpdateUserStatusResponseDto>('/user/status/list', data);
	},

	/**
	 * 유저 삭제
	 * Swagger: DELETE /user/list, body: DeleteUserListRequestDto { userIdList }
	 */
	deleteUsers: async (data: DeleteUserListRequestDto): Promise<DeleteUserListResponseDto> => {
		return await apiClient.delete<DeleteUserListResponseDto>('/user/list', data);
	},

	/**
	 * 유저 기관 정보 삭제 (기관에서 내보내기)
	 * PATCH /user/organization/remove, body: { userIdList }
	 */
	removeUserOrganization: async (data: RemoveUserOrganizationRequestDto): Promise<RemoveUserOrganizationResponseDto> => {
		return await apiClient.patch<RemoveUserOrganizationResponseDto>('/user/organization/remove', data);
	},

	/**
	 * 게스트 계정 일괄 생성 - 화면 입력 방식
	 * POST /user/bulk-create-ui — application/json
	 */
	createGuestAccountsUi: async (data: CreateGuestAccountsRequestDto): Promise<CreateGuestAccountsResponseDto> => {
		const raw = await apiClient.post<unknown>('/user/bulk-create-ui', data);
		return normalizeBulkCreateGuestResponse(raw) as CreateGuestAccountsResponseDto;
	},

	/**
	 * 유저 일괄 등록 템플릿 다운로드
	 */
	downloadBulkUploadTemplate: async (): Promise<DownloadUserBulkUploadTemplateResponseDto> => {
		return await apiClient.get<DownloadUserBulkUploadTemplateResponseDto>(
			'/user/bulk-upload-template'
		);
	},

	/**
	 * 유저 일괄 생성 - 엑셀 파일 업로드 방식
	 * POST /user/bulk-create — multipart/form-data
	 * BulkCreateUsersRequestDto: { organizationId, file, validityDays? }
	 */
	bulkCreateUsers: async (data: BulkCreateUsersRequestDto): Promise<BulkCreateUsersResponseDto> => {
		const formData = new FormData();
		formData.append('organizationId', data.organizationId.toString());
		formData.append('file', data.file);
		if (data.validityDays != null) formData.append('validityDays', String(data.validityDays));
		const raw = await apiClient.post<unknown>('/user/bulk-create', formData);
		return normalizeBulkCreateGuestResponse(raw) as BulkCreateUsersResponseDto;
	},

	/**
	 * 유저 리스트 엑셀 추출
	 */
	exportUserListExcel: async (
		data: ExportUserListExcelRequestDto
	): Promise<ExportUserListExcelResponseDto> => {
		return await apiClient.post<ExportUserListExcelResponseDto>(
			`/user/export/excel`,
			data
		);
	},

	/**
	 * 사용자 정보 수정 (관리자용)
	 * PATCH /user/admin/update-user — 허용 필드만 전송 (그 외는 400 property X should not exist)
	 */
	updateUserInfo: async (data: UpdateUserInfoRequestDto): Promise<UpdateUserInfoResponseDto> => {
		const body: Record<string, unknown> = { userId: data.userId };
		if (typeof data.role === 'number') {
			body.role = data.role;
		}
		if (typeof data.isActive === 'boolean') {
			body.isActive = data.isActive;
		}
		if (data.affiliation !== undefined) {
			body.affiliation = String(data.affiliation).trim();
		}
		if (data.phoneNumber !== undefined) {
			body.phoneNumber = String(data.phoneNumber).trim();
		}
		if (data.position !== undefined) {
			body.position = String(data.position).trim();
		}
		if (data.jobTitle !== undefined) {
			body.jobTitle = data.jobTitle;
		}
		if (data.customJobTitle !== undefined) {
			body.customJobTitle = String(data.customJobTitle).trim();
		}
		if (data.newPassword !== undefined && data.newPassword !== null) {
			const pw = String(data.newPassword).trim();
			if (pw.length > 0) body.newPassword = pw;
		}
		if (data.guestAccountExpiresAt !== undefined) {
			if (data.guestAccountExpiresAt === null) {
				body.guestAccountExpiresAt = null;
			} else {
				const g = String(data.guestAccountExpiresAt).trim();
				if (g.length > 0) body.guestAccountExpiresAt = g;
			}
		}
		return await apiClient.patch<UpdateUserInfoResponseDto>('/user/admin/update-user', body);
	},

	/**
	 * 사용자 비밀번호 변경 (관리자용)
	 * Swagger: POST /user/change-password-admin, body: { userId, newPassword }
	 */
	updateUserPassword: async (userId: number, newPassword: string): Promise<UpdateUserInfoResponseDto> => {
		return await apiClient.post<UpdateUserInfoResponseDto>('/user/change-password-admin', {
			userId,
			newPassword,
		});
	},

	/**
	 * 슈퍼관리자용 유저 권한 변경
	 */
	updateUserRoleByAdmin: async (data: UpdateUserRoleRequestDto): Promise<UpdateUserRoleResponseDto> => {
		return await apiClient.patch<UpdateUserRoleResponseDto>('/user/role/admin', data);
	},

	/**
	 * 기관관리자용 유저 권한 변경
	 */
	updateUserRoleByOrganization: async (data: UpdateUserRoleRequestDto): Promise<UpdateUserRoleResponseDto> => {
		return await apiClient.patch<UpdateUserRoleResponseDto>('/user/role/organization', data);
	},
};
